import { ConditionalLink } from "components/conditionalLink";
import {
  DnDArea,
  File,
  UploadLabel,
} from "components/design/UploadFileSection";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import dayjs from "dayjs";
import { useAbortController } from "hooks/useAbortController";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import {
  useCreateEntityInvoiceMutation,
  useExtractInvoiceFromImageMutation,
} from "store/apis/invoices";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import aiExtractingImage from "static/images/AiLoad.gif";

export const UploadInvoiceFromPDF = ({ isOpen, close }: ModalProps) => {
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const { alertToast, successToast } = useToast();
  const history = useHistory();
  const [file, setFile] = useState<File | null>(null);
  const [extractInvoice, extractState] = useExtractInvoiceFromImageMutation();
  const { link: constructLink } = useConstructInternalLink();

  const [createInvoice] = useCreateEntityInvoiceMutation();

  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId }
  );

  const uploadController = useAbortController({
    onAbort: () => {
      alertToast({ message: "Upload cancelled" });
    },
  });

  const onClose = () => {
    close();
    setFile(null);
    extractState.reset();
  };

  const navigateToInvoice = (uuid: string) => {
    history.push(constructLink(`/books/invoices-and-customers/${uuid}`));
  };

  const createBlankInvoice = async () => {
    try {
      const invoice = await createInvoice({
        groupId: groupId!,
        entityId: entityId,
      }).unwrap();

      navigateToInvoice(invoice.uuid);
    } catch (error) {
      alertToast(
        {
          message:
            (error as BackendError).data?.error?.message ??
            "Something went wrong",
        },
        error as Error
      );
    }
  };

  const onDrop = async (files: File[]) => {
    if (!files.length) return;
    try {
      setFile(files[0]);
      const result = await extractInvoice({
        entityId,
        groupId: groupId!,
        files: [files[0]],
      }).unwrap();

      successToast({ message: "Invoice extracted successfully" });
      navigateToInvoice(result.uuid);
      onClose();
    } catch (error) {
      alertToast({
        message: "AI extraction failed. Creating a blank invoice instead.",
      });
      void createBlankInvoice();
      setFile(null);
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content size="large">
        <Modal.Header>
          <Modal.Title>Upload Invoice</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {extractState.isLoading ? (
            <div className="t-flex t-flex-col t-items-center t-justify-center t-gap-1.5 t-py-8">
              <div className="t-flex t-gap-1.5">
                <img
                  src={aiExtractingImage}
                  alt="AI Extracting"
                  className="t-w-6 t-h-6"
                />
                <p className="t-m-0 t-text-subtext t-bg-ai-gradient t-bg-clip-text t-fill-surface-transparent t-text-surface-transparent">
                  AI Agent is extracting details
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="t-mb-1.5">
                <UploadLabel>
                  Upload a PDF or image of your invoice. AI will automatically
                  extract details to create a new invoice.
                </UploadLabel>
              </div>

              <div className="t-flex t-flex-col t-gap-5">
                {file ? (
                  <File
                    file={file}
                    onCancel={uploadController.abort}
                    onDelete={() => setFile(null)}
                    isUploading={extractState.isLoading}
                    isUploaded={extractState.isSuccess}
                  />
                ) : (
                  <DnDArea
                    onDrop={onDrop}
                    accept={{
                      "application/pdf": [".pdf"],
                      "image/jpeg": [".jpg", ".jpeg"],
                      "image/png": [".png"],
                    }}
                    subText="File Format: pdf / jpg / png"
                  />
                )}

                <Banner
                  type="info"
                  title="AI Extraction"
                  description="AI will automatically extract invoice details including customer information, amount, date, and line items from the uploaded file."
                />

                {bookkeepingSettings?.accounting_lock_date && (
                  <Banner
                    type="neutral"
                    title={
                      <span>
                        Your financials are locked until{" "}
                        {dayjs(bookkeepingSettings.accounting_lock_date).format(
                          "MMM DD, YYYY"
                        )}
                      </span>
                    }
                    description={
                      <span>
                        If the extracted invoice date is before this, the upload
                        will fail. You can either change the invoice dates
                        manually or update the lock date in{" "}
                        <ConditionalLink
                          to={constructLink("/books/books-settings")}
                          className="t-text-text-30 !t-underline"
                        >
                          settings
                        </ConditionalLink>
                        .
                      </span>
                    }
                  />
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Modal.RawClose asChild>
            <Button disabled={extractState.isLoading}>Cancel</Button>
          </Modal.RawClose>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
